import { Typography, message, Input, Row, Col, Card, Button, Space, Tooltip, Dropdown, Menu } from "antd";
import { UserOutlined, PlusOutlined, ExportOutlined, MoreOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import ClientesIndex from "./ClientesIndex";
import ClienteForm from "../verifications/forms/general/ClienteForm";
import axios from "axios";
import { useAuth } from "../../layout/auth";

const CARD = {
  background: "#fff",
  border: "0.5px solid rgba(0,0,0,0.08)",
  borderRadius: "12px",
};

export default function Clients() {
  const [clientes, setClientes] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [search, setSearch] = useState("");
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clienteFormVisible, setClienteFormVisible] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);

  const auth = useAuth();

  const fetchClientes = () => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/clientes/view/list`) // Using dedicated endpoint
      .then((res) => {
        setClientes(res.data);
        setFilteredClientes(res.data);
        setCurrentData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.message);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchClientes();
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = clientes.filter((cliente) => {
        return cliente?.NombreCliente?.toLowerCase().includes(search.toLowerCase());
      });
      setFilteredClientes(filtered);
      setCurrentData(filtered);
    } else {
      setFilteredClientes(clientes);
      setCurrentData(clientes);
    }
  }, [search, clientes]);

  const handleEdit = (id) => {
    setSelectedCliente(id);
    setClienteFormVisible(true);
  };

  const handleAdd = () => {
    setSelectedCliente(null);
    setClienteFormVisible(true);
  };

  const handleExport = () => {
    if (!currentData?.length) return message.warning("No hay datos para exportar");

    const headers = ["Nombre", "Apodo/Alias", "Teléfono", "Correo Electrónico", "Verificaciones"];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return "";
      const s = String(val);
      if (s.includes(",") || s.includes('"') || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const rows = currentData.map((v) => [
      escapeCSV(v.NombreCliente),
      escapeCSV(v.ApodoAlias),
      escapeCSV(v.Telefono),
      escapeCSV(v.CorreoElectronico),
      escapeCSV(v.VerificacionesCount),
    ]);

    const csvContent = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clientes_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success("Exportado correctamente");
  };

  const mobileMenuItems = [
    {
      key: "exportar",
      label: "Exportar",
      icon: <ExportOutlined />,
      onClick: handleExport,
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {/* ── Header card ── */}
      <div style={{ ...CARD, padding: 0 }}>
        {/* Title row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            padding: "18px 20px 14px",
            borderBottom: "0.5px solid rgba(0,0,0,0.06)",
            flexWrap: "wrap",
          }}
        >
          {/* Icon badge */}
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: "rgba(24,144,255,0.08)",
              border: "0.5px solid rgba(24,144,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <UserOutlined style={{ fontSize: 17, color: "#1677ff" }} />
          </div>

          {/* Title + subtitle */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "rgba(0,0,0,0.88)", lineHeight: 1.3 }}>Clientes</div>
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.38)", marginTop: 1 }}>Administración de cartera de clientes</div>
          </div>

          {/* Desktop actions */}
          <Space size={8} className="vf-desktop-actions" style={{ flexShrink: 0 }}>
            <Tooltip title="Agregar cliente">
              <Button type="primary" icon={<PlusOutlined />} style={{ fontSize: 13, fontWeight: 500 }} onClick={handleAdd}>
                Agregar
              </Button>
            </Tooltip>
            <Tooltip title="Exportar como CSV">
              <Button icon={<ExportOutlined />} style={{ fontSize: 13 }} onClick={handleExport}>
                Exportar
              </Button>
            </Tooltip>
          </Space>

          {/* Mobile actions */}
          <Space size={8} className="vf-mobile-actions" style={{ flexShrink: 0 }}>
            <Button type="primary" icon={<PlusOutlined />} style={{ fontSize: 13, fontWeight: 500 }} onClick={handleAdd}>
              Agregar
            </Button>
            <Dropdown overlay={<Menu items={mobileMenuItems} />} trigger={["click"]} placement="bottomRight">
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        </div>

        {/* Search row */}
        <div style={{ padding: "12px 20px" }}>
          <Input.Search
            placeholder="Buscar por nombre…"
            onSearch={(value) => setSearch(value)}
            onChange={(e) => {
              if (!e.target.value) setSearch("");
            }}
            allowClear
            loading={loading}
            style={{ maxWidth: 380 }}
          />
        </div>
      </div>

      {/* ── Table card ── */}
      <div style={{ ...CARD, overflow: "hidden" }}>
        <ClientesIndex clientes={filteredClientes} loading={loading} onEdit={handleEdit} onDataChange={setCurrentData} />
      </div>

      {/* Responsive */}
      <style>{`
        .vf-mobile-actions  { display: none !important; }
        .vf-desktop-actions { display: flex !important; }
        @media (max-width: 600px) {
          .vf-mobile-actions  { display: flex !important; }
          .vf-desktop-actions { display: none !important; }
        }
      `}</style>

      <ClienteForm
        visible={clienteFormVisible}
        setVisible={setClienteFormVisible}
        setClientes={setClientes}
        cliente={selectedCliente}
        onSuccess={() => {
          fetchClientes();
          setClienteFormVisible(false);
        }}
      />
    </div>
  );
}
